import type { FixedRoute, FixedRouteSegment } from '../../types/fixedRoutes';

/**
 * Build the inbound "return" name for a route, avoiding a doubled "(Return)" suffix.
 */
export function makeReturnRouteName(name: string): string {
  return /\(return\)/i.test(name) ? name : `${name} (Return)`;
}

/**
 * Build a reversed sibling of `source` — the opposite-direction (inbound) pattern (F2).
 *
 * The stop order is reversed; each reversed segment seeds its travel time/distance from the
 * matching forward segment as an estimate (real reversed times can differ on one-way streets,
 * so geometry is cleared to force a fresh road recalculation on render). The two routes are
 * linked via `linkedRouteId`; this one is tagged `directionId: 'inbound'`.
 *
 * Backward-compatible: only adds optional fields; the result is independently editable.
 */
export function buildReversedRoute(source: FixedRoute, newId: string): FixedRoute {
  const reversedStops = [...source.stops].reverse();
  const reversedSegments: FixedRouteSegment[] = [];
  for (let i = 0; i < reversedStops.length - 1; i++) {
    const fromStopId = reversedStops[i].stopId;
    const toStopId = reversedStops[i + 1].stopId;
    // Forward segment for this pair runs toStopId -> fromStopId; reuse its time as an estimate.
    const forward = source.segments.find((s) => s.fromStopId === toStopId && s.toStopId === fromStopId);
    reversedSegments.push({
      fromStopId,
      toStopId,
      travelTimeMinutes: forward?.travelTimeMinutes ?? 5,
      distanceMeters: forward?.distanceMeters,
    });
  }

  return {
    ...source,
    id: newId,
    name: makeReturnRouteName(source.name),
    stops: reversedStops,
    segments: reversedSegments,
    geometry: undefined,
    waypoints: undefined,
    waypointStopIndices: undefined,
    isManuallyEdited: false,
    directionId: 'inbound',
    linkedRouteId: source.id,
  };
}
